import { postPayURequest } from './AppApi';
import { toast } from 'react-toastify';

const STORAGE_KEY = 'upi_atm_pending_checks';
const RESULTS_KEY = 'upi_atm_results';
const CHECK_API = 'upi/instantpayatm/checkStatus';
const MAX_RETRIES = 3;

/**
 * Save a pending background check
 */
export const saveUpiAtmBackgroundCheck = (ipayId, amount, mobile, delaySeconds = 65) => {
  try {
    const pendingChecks = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    pendingChecks[ipayId] = {
      amount,
      mobile,
      executeAt: Date.now() + (delaySeconds * 1000),
      retryCount: 0,
      createdAt: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingChecks));
    console.log('✅ [UPI ATM] Background check saved:', ipayId, '| execute in', delaySeconds, 's');
    return true;
  } catch (e) {
    console.error('[UPI ATM] Error saving background check:', e);
    return false;
  }
};

/**
 * Remove a background check
 */
export const removeUpiAtmBackgroundCheck = (ipayId) => {
  try {
    const pendingChecks = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    if (pendingChecks[ipayId]) {
      delete pendingChecks[ipayId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingChecks));
      console.log('🗑️ [UPI ATM] Removed check:', ipayId);
    }
  } catch (e) {
    console.error('[UPI ATM] Error removing background check:', e);
  }
};

/**
 * Check if a status message indicates a final/refund state
 */
const isFinalState = (status, message) => {
  if (status === 'SUCCESS' || status === 'FAILED' || status === 'EXPIRED') {
    return true;
  }
  
  // Check message for refund/return keywords even if status is INITIATED
  const msg = (message || '').toLowerCase();
  const refundKeywords = ['refund', 'return', 'reversed', 'failed', 'declined', 'rejected', 'not completed', 'cancelled'];
  return refundKeywords.some(keyword => msg.includes(keyword));
};

/**
 * Get display status for the result modal
 */
const getResultInfo = (status, message, payerName, amount) => {
  const msg = (message || '').toLowerCase();
  const isRefunded = msg.includes('refund') || msg.includes('return') || msg.includes('reversed');
  
  if (status === 'SUCCESS') {
    return {
      title: '✅ Payment Successful - UPI QR',
      message: `Amount: ₹${amount} | Payer: ${payerName}\nPlease hand over cash to customer.`,
      isSuccess: true
    };
  }
  
  if (status === 'FAILED' || (status === 'INITIATED' && isRefunded)) {
    return {
      title: '❌ Payment Failed - UPI QR',
      message: message || 'Transaction was not completed.',
      isSuccess: false
    };
  }
  
  if (status === 'EXPIRED') {
    return {
      title: '⏰ QR Expired - UPI QR',
      message: 'Customer did not complete payment in time.',
      isSuccess: false
    };
  }
  
  return {
    title: 'ℹ️ Payment Pending - UPI QR',
    message: 'Payment is still being processed.',
    isSuccess: false
  };
};

/**
 * Check and execute all pending background checks
 * Call this on every page load (in App.js or layout component)
 */
export const checkAllPendingUpiAtmChecks = async () => {
  try {
    const pendingChecks = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const keys = Object.keys(pendingChecks);
    
    if (keys.length === 0) return;
    
    console.log('📋 [UPI ATM] Found', keys.length, 'pending checks');
    
    const now = Date.now();
    const remaining = {};
    const results = JSON.parse(localStorage.getItem(RESULTS_KEY) || '[]');

    for (const ipayId of keys) {
      const check = pendingChecks[ipayId];
      
      // Check if it's time to execute
      if (!check.executeAt || now < check.executeAt) {
        // Not yet time - keep for later
        remaining[ipayId] = check;
        console.log('⏱️ [UPI ATM] Not yet time for:', ipayId?.substring(0, 14) + '...');
        continue;
      }
      
      console.log('⏰ [UPI ATM] Executing check for:', ipayId?.substring(0, 14) + '...');
      
      try {
        const res = await postPayURequest(CHECK_API, {
          ipayId,
          devicetype: 'W',
          latitude: "28.50937",
          longitude: "77.29727"
        });

        if (res?.success && res?.data) {
          const status = res.data.qrStatus;
          const message = res.data.qrStatusMsg || '';
          const payerName = res.data.payer?.name || 'Customer';
          const amount = res.data.amount || check.amount || '0';

          console.log('📢 [UPI ATM] Status for', ipayId?.substring(0, 14) + '...', ':', status, '|', message);

          if (isFinalState(status, message)) {
            // ✅ FINAL STATE - Don't retry, save result
            const resultInfo = getResultInfo(status, message, payerName, amount);
            results.push({
              ipayId,
              ...resultInfo,
              timestamp: Date.now()
            });
            
            console.log('🔒 [UPI ATM] Final state for:', ipayId?.substring(0, 14) + '...', '- removed from pending');
            
            if (resultInfo.isSuccess) {
              toast.success('💰 Payment received!');
            }
          } else if (status === 'INITIATED') {
            // ⏳ GENUINELY PENDING - Retry up to MAX_RETRIES
            const retryCount = (check.retryCount || 0) + 1;
            
            if (retryCount > MAX_RETRIES) {
              // Max retries reached - stop and save as pending result
              console.log('🛑 [UPI ATM] Max retries (' + MAX_RETRIES + ') reached for:', ipayId?.substring(0, 14) + '...');
              results.push({
                ipayId,
                title: 'ℹ️ Payment Status Unknown - UPI QR',
                message: 'Payment status could not be determined after multiple checks. Please check manually.',
                timestamp: Date.now()
              });
            } else {
              // Still retrying
              remaining[ipayId] = {
                ...check,
                executeAt: Date.now() + 30000, // Retry in 30 seconds
                retryCount: retryCount
              };
              console.log('⏳ [UPI ATM] Still pending for:', ipayId?.substring(0, 14) + '...', '| retry', retryCount + '/' + MAX_RETRIES);
            }
          } else {
            // Unknown status - treat as final to be safe
            console.log('⚠️ [UPI ATM] Unknown status:', status, 'for:', ipayId?.substring(0, 14) + '...');
            results.push({
              ipayId,
              title: 'ℹ️ Payment Status Update - UPI QR',
              message: `Status: ${status}\n${message}`,
              timestamp: Date.now()
            });
          }
        } else {
          // API returned success=false
          console.log('⚠️ [UPI ATM] API returned success=false for:', ipayId?.substring(0, 14) + '...');
          handleRetryOrStop(remaining, check, ipayId, results);
        }
      } catch (err) {
        console.error('❌ [UPI ATM] API error for', ipayId?.substring(0, 14) + '...', ':', err.message);
        handleRetryOrStop(remaining, check, ipayId, results);
      }
    }

    // Save remaining checks and results
    localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
    localStorage.setItem(RESULTS_KEY, JSON.stringify(results));
    
    console.log('📊 [UPI ATM] Remaining pending:', Object.keys(remaining).length, '| Results:', results.length);
    
  } catch (e) {
    console.error('[UPI ATM] Error in checkAllPendingUpiAtmChecks:', e);
  }
};

/**
 * Handle retry or stop for failed API calls
 */
const handleRetryOrStop = (remaining, check, ipayId, results) => {
  const retryCount = (check.retryCount || 0) + 1;
  
  if (retryCount > MAX_RETRIES) {
    console.log('🛑 [UPI ATM] Max retries reached for:', ipayId?.substring(0, 14) + '...');
    results.push({
      ipayId,
      title: '⚠️ Status Check Failed - UPI QR',
      message: 'Unable to verify payment status. Please check manually.',
      timestamp: Date.now()
    });
  } else {
    remaining[ipayId] = {
      ...check,
      executeAt: Date.now() + 15000, // Retry in 15 seconds
      retryCount: retryCount
    };
    console.log('🔄 [UPI ATM] Will retry for:', ipayId?.substring(0, 14) + '...', '| retry', retryCount + '/' + MAX_RETRIES);
  }
};

/**
 * Get pending results to display
 */
export const getUpiAtmResults = () => {
  try {
    return JSON.parse(localStorage.getItem(RESULTS_KEY) || '[]');
  } catch (e) {
    return [];
  }
};

/**
 * Clear displayed results
 */
export const clearUpiAtmResults = () => {
  localStorage.removeItem(RESULTS_KEY);
};

/**
 * Clear all pending checks (for admin/debug purposes)
 */
export const clearAllPendingChecks = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(RESULTS_KEY);
  console.log('🧹 [UPI ATM] All pending checks and results cleared');
};

/**
 * Get count of pending checks (for debugging)
 */
export const getPendingCheckCount = () => {
  try {
    const pendingChecks = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return Object.keys(pendingChecks).length;
  } catch (e) {
    return 0;
  }
};